import React, { ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CloseButton } from '../CloseButton/CloseButton';
import Divider from '../Divider/Divider';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import SectionTitle from '../SectionTitle/SectionTitle';

interface ButtonProps {
  label: string;
  onClick: () => void;
}

interface SlideOverProps {
  isOpen: boolean;
  setIsOpen: any;
  title?: string;
  primaryButton?: ButtonProps;
  secondaryButton?: ButtonProps;
  children: ReactNode;
  isLoading?: boolean;
  hasErrors?: boolean;
  /** When true, stack above Modal overlays (Modal.css z-index 99/101). Default keeps modal on top. */
  aboveModal?: boolean;
}

const SlideOver: React.FC<SlideOverProps> = ({
  isOpen,
  setIsOpen,
  title,
  primaryButton,
  secondaryButton,
  children,
  isLoading = false,
  hasErrors = false,
  aboveModal = false,
}) => {

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    return null;
  }

  const overlayZIndex = aboveModal ? 'z-[102]' : 'z-50';

  const slideOverContent = (
    <div
      className={`fixed inset-0 ${overlayZIndex} overflow-hidden !mt-0 m-0`}
      aria-labelledby="slide-over-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

        <section className="absolute inset-y-0 right-0 pl-0 md:pl-10 max-w-full flex justify-end" aria-labelledby="slide-over-heading">
          <div className="relative w-full sm:w-[28rem] max-w-full flex-shrink-0 transform transition ease-in-out duration-1000 translate-x-0 ease-out">
            <div className="h-full flex flex-col pt-4 pb-6 md:py-6 bg-white shadow-xl overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable] space-y-5">
              <div className="px-4 sm:px-6">
                <div className="flex items-start justify-between">
                  <SectionTitle text={title || ''} />
                  <div className="ml-3 h-7 flex items-center">
                    <CloseButton fn={() => setIsOpen(false)} />
                  </div>
                </div>
              </div>
              <Divider />
              <div className="relative flex-1 px-4 sm:px-6">
                {children}
              </div>
              <div className="px-4 sm:px-6">
                <div className="flex justify-between items-center">
                  {secondaryButton ? (
                    <SecondaryButton fn={secondaryButton.onClick} text={secondaryButton.label} isDisabled={isLoading} />
                  ) : (
                    <div></div>
                  )}
                  {primaryButton && (
                    <PrimaryButton fn={primaryButton.onClick} text={primaryButton.label} isDisabled={isLoading || hasErrors} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  return ReactDOM.createPortal(slideOverContent, modalRoot);
};

export default SlideOver;
